import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getCduUser } from '@gef-ui/cdu-user/selectors';
import { getDelegations, getRepresentedUserLogin } from '@gef-ui/delegations/selectors';
import { getLabelMessage } from '@gef-ui/localization/messages';
import { AppbarUser, CloseableUserItem } from '@gef-ui/material/components/AppbarUser';
import UserGroupRegular from '@gef-ui/material-icons/UserGroupRegular';
import UserRegular from '@gef-ui/material-icons/UserRegular';
import { getAppContext } from '@gef-ui/redux-common/selectors/appContext';

import { COMPANY_STR } from 'constants/general';
import { generateUrl } from 'features/common/fetch/utils';
import { beforeLogout } from 'features/other/navbar/actions';
import { withoutActualUser } from 'features/views/navbar/utils';

const User = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { userLogon, userFullName } = useSelector(getCduUser);
	const representedUserLogin = useSelector(getRepresentedUserLogin);
	const appContext = useSelector(getAppContext);
	const delegations = useSelector(getDelegations);

	const allowedCompanies = appContext?.originalUserInfo?.allowedCompanies;

	const originalUserLogon = appContext?.originalUserInfo?.userLogon;

	const userDelegations = useMemo(
		() => withoutActualUser(delegations, appContext, representedUserLogin, allowedCompanies),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[delegations, appContext?.originalUserInfo, representedUserLogin, allowedCompanies],
	);

	const handleLogout = useCallback(() => {
		dispatch(beforeLogout());
		navigate('/logout');
	}, [dispatch, navigate]);

	const redirect = useCallback(
		(url) => async () => {
			window.location.href = url;
		},
		[],
	);

	return (
		<AppbarUser
			userName={`${userFullName || userLogon} ${
				!isNilOrEmpty(representedUserLogin) ? getLabelMessage('common:representativeFor') : ''
			}`}
			handleLogout={handleLogout}
		>
			{!isNilOrEmpty(userDelegations) && (
				<Box
					sx={{
						maxHeight: 259,
						overflowY: 'scroll',
						'&::-webkit-scrollbar': {
							width: 10,
						},
						'&::-webkit-scrollbar-track': {
							background: 'transparent',
						},
						'&::-webkit-scrollbar-thumb': {
							background: (theme) => theme.palette.grey[300],
							padding: 0,
							borderRadius: 0,
						},
					}}
				>
					<>
						<Typography sx={{ color: 'text.primary', opacity: 0.5, paddingX: 2, pb: 1 }} variant="body2">
							{getLabelMessage('site:loginAs')}
						</Typography>
						<Divider sx={{ borderStyle: 'dashed' }} />
					</>
					{userDelegations.map((item, i) => (
						<CloseableUserItem
							key={i}
							onClick={redirect(
								generateUrl(
									window.location.origin,
									null,
									{
										company: COMPANY_STR.CP,
										representativeFor: item.userLogon,
									},
									false,
								),
							)}
							sx={{
								py: 1,
							}}
						>
							{item.userLogon === originalUserLogon ? (
								<UserRegular sx={{ mr: 2, fontSize: '1.2rem' }} />
							) : (
								<UserGroupRegular sx={{ mr: 2, fontSize: '1.2rem' }} />
							)}
							{item.surname} {item.firstName}
						</CloseableUserItem>
					))}
				</Box>
			)}
		</AppbarUser>
	);
};

export default User;
